import React, { useState, Component } from 'react';
import { SettingsIcon, ShieldCheckIcon, UsersIcon, KeyRoundIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, LockIcon, FingerprintIcon, ScrollTextIcon, UserCogIcon } from 'lucide-react';
type SubTab = 'gerer' | 'analytiques' | 'configurer';
type GererSection = 'roles' | 'autorisations' | 'utilisateurs';
type ConfigSection = 'securite' | 'authentification' | 'journalisation';
interface Role {
  id: string;
  nom: string;
  description: string;
  statut: 'actif' | 'inactif';
}
interface Utilisateur {
  id: string;
  nom: string;
  email: string;
  roleId: string;
}
const ROLES: Role[] = [{
  id: 'admin',
  nom: 'Administrateur',
  description: 'Accès complet à tous les modules et paramètres',
  statut: 'actif'
}, {
  id: 'comm',
  nom: 'Gestionnaire Communications',
  description: 'Création et diffusion des avis municipaux',
  statut: 'actif'
}, {
  id: 'agent',
  nom: 'Agent Requêtes',
  description: 'Traitement des requêtes citoyennes',
  statut: 'actif'
}, {
  id: 'lecture',
  nom: 'Lecture seule',
  description: 'Consultation des données sans modification',
  statut: 'inactif'
}];
const MODULES: {
  id: string;
  label: string;
}[] = [{
  id: 'communications',
  label: 'Communications'
}, {
  id: 'requetes',
  label: 'Requêtes'
}, {
  id: 'id',
  label: 'ID'
}, {
  id: 'collectes',
  label: 'Collectes'
}, {
  id: 'compteurs',
  label: 'Compteurs'
}, {
  id: 'atna',
  label: 'ATNA AI'
}, {
  id: 'parametres',
  label: 'Paramètres'
}];
const ACTIONS = ['Voir', 'Créer', 'Modifier', 'Supprimer'] as const;
const INITIAL_USERS: Utilisateur[] = [{
  id: '1',
  nom: 'Hugo Langlais',
  email: 'hugo.langlais@windsor.ca',
  roleId: 'admin'
}, {
  id: '2',
  nom: 'Marie Tremblay',
  email: 'marie.tremblay@windsor.ca',
  roleId: 'comm'
}, {
  id: '3',
  nom: 'Jean Dupont',
  email: 'jean.dupont@windsor.ca',
  roleId: 'agent'
}, {
  id: '4',
  nom: 'Sarah Connor',
  email: 'sarah.connor@windsor.ca',
  roleId: 'agent'
}, {
  id: '5',
  nom: 'Belanor Waesceran',
  email: 'belanor.w@windsor.ca',
  roleId: 'lecture'
}];
// Default permissions per role
function buildInitialPermissions(): Record<string, boolean> {
  const perms: Record<string, boolean> = {};
  MODULES.forEach(m => {
    ACTIONS.forEach(a => {
      // admin: everything; lecture: only Voir; others: Voir+Créer+Modifier on a couple modules
      perms[`admin:${m.id}:${a}`] = true;
      perms[`lecture:${m.id}:${a}`] = a === 'Voir';
      perms[`comm:${m.id}:${a}`] = m.id === 'communications' ? a !== 'Supprimer' : a === 'Voir';
      perms[`agent:${m.id}:${a}`] = m.id === 'requetes' ? a !== 'Supprimer' : a === 'Voir';
    });
  });
  return perms;
}
interface SwitchProps {
  checked: boolean;
  onChange: () => void;
}
function Switch({
  checked,
  onChange
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} onClick={onChange} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-6111" data-mp="31uy9nzxaes1k">
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-6112" data-mp="3ku2dmu9z1vmd" />
    </button>;
}
export function SettingsModule() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('gerer');
  const [gererSection, setGererSection] = useState<GererSection>('roles');
  const [configSection, setConfigSection] = useState<ConfigSection>('securite');
  const [roles] = useState<Role[]>(ROLES);
  const [users, setUsers] = useState<Utilisateur[]>(INITIAL_USERS);
  const [permissions, setPermissions] = useState<Record<string, boolean>>(buildInitialPermissions);
  const [selectedRole, setSelectedRole] = useState<string>('admin');
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: 'Gérer'
  }, {
    id: 'analytiques',
    label: 'Analytiques'
  }, {
    id: 'configurer',
    label: 'Configurer'
  }];
  const gererSections: {
    id: GererSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
  }[] = [{
    id: 'roles',
    label: 'Rôles',
    icon: ShieldCheckIcon
  }, {
    id: 'autorisations',
    label: 'Autorisations',
    icon: KeyRoundIcon
  }, {
    id: 'utilisateurs',
    label: 'Utilisateurs',
    icon: UsersIcon
  }];
  const roleName = (id: string) => roles.find(r => r.id === id)?.nom ?? '—';
  const countPerms = (roleId: string) => Object.keys(permissions).filter(k => k.startsWith(`${roleId}:`) && permissions[k]).length;
  const countUsers = (roleId: string) => users.filter(u => u.roleId === roleId).length;
  const togglePerm = (key: string) => setPermissions(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  // ---------- GÉRER ----------
  const renderRoles = () => <div className="space-y-4" data-id="element-6113" data-mp="2ysh1b6nv582x">
      <div className="flex items-center justify-between" data-id="element-6114" data-mp="2yredx8pr1nrr">
        <div data-id="element-6115" data-mp="2uqvjr80dw5me">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-6116" data-mp="2uqm1k6lkz2r9">Rôles</h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-6117" data-mp="2pr99n7fur5ht">
            Définissez des rôles puis attribuez-leur des autorisations.
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-6118" data-mp="2ksog1qv3vk2c">
          <PlusIcon className="w-4 h-4" data-id="element-6119" data-mp="j1bdv9kivq9z" /> Nouveau rôle
        </button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-6120" data-mp="e1p08z0s880n">
        <table className="w-full text-left" data-id="element-6121" data-mp="dzm3zx91dinh">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-6122" data-mp="28hnc7c0bovhv">
            <tr data-id="element-6123" data-mp="2d8nbgnwwzpnj">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-6124" data-mp="2d95h2b5gvg84">
                Rôle
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-6125" data-mp="1ylbx6e9clrjq">
                Autorisations
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-6126" data-mp="2r853rd9f5l1n">
                Usagers
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-6127" data-mp="2w55azri08tuc">
                Statut
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-6128" data-mp="2m8k410mfd8nv">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-6129" data-mp="1auof505pxljg">
            {roles.map(r => <tr key={r.id} className="hover:bg-slate-50" data-id="element-6130" data-mp="13adtiz4gmub3">
                <td className="px-4 py-3" data-id="element-6131" data-mp="138t0xf1phlfv">
                  <div className="font-semibold text-slate-900 text-sm" data-id="element-6132" data-mp="yc2k2pi2ulte">
                    {r.nom}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-6133" data-mp="1d7ih93dyexu0">
                    {r.description}
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-6134" data-mp="187w5yzhorrnu">
                  {countPerms(r.id)}
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-6135" data-mp="fm4au62jmmo6">
                  {countUsers(r.id)}
                </td>
                <td className="px-4 py-3" data-id="element-6136" data-mp="ubsfnh9xo3gn">
                  <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${r.statut === 'actif' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-6137" data-mp="pi7oo96rc9qy">
                    {r.statut}
                  </span>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-6138" data-mp="3cfhh3s4tij3p">
                  <div className="flex items-center justify-end gap-2" data-id="element-6139" data-mp="3cg9aqxeuu6rc">
                    <button onClick={() => {
                  setSelectedRole(r.id);
                  setGererSection('autorisations');
                }} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Configurer les autorisations" data-id="element-6140" data-mp="3hdrd24yd9nqd">
                      <KeyRoundIcon className="w-4 h-4" data-id="element-6141" data-mp="40z379s749c6" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Modifier" data-id="element-6142" data-mp="8zipjbapbn52">
                      <PencilIcon className="w-4 h-4" data-id="element-6143" data-mp="hyxda2zhhjlv" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Supprimer" data-id="element-6144" data-mp="22i9bsc912ib3">
                      <TrashIcon className="w-4 h-4" data-id="element-6145" data-mp="1tg9steegd45i" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderAutorisations = () => <div className="space-y-5" data-id="element-6146" data-mp="2labt7pthu2me">
      <div data-id="element-6147" data-mp="2h6vwyulc64a8">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-6148" data-mp="2h6du3f464rtc">Autorisations</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-6149" data-mp="2cfxeppgsew4c">
          Choisissez un rôle et accordez-lui des autorisations par module.
        </p>
      </div>

      {/* Role selector */}
      <div className="flex flex-wrap gap-2" data-id="element-6150" data-mp="27eq7brik3yy7">
        {roles.map(r => <button key={r.id} onClick={() => setSelectedRole(r.id)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${selectedRole === r.id ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-6151" data-mp="34xrn7na58rfd">
            {r.nom}
          </button>)}
      </div>

      {/* Permission matrix */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-6152" data-mp="w0u0j2epqwyk">
        <table className="w-full text-left" data-id="element-6153" data-mp="s3o6586pu4ke">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-6154" data-mp="160t5kpbxc56o">
            <tr data-id="element-6155" data-mp="15z00vfkvfv1b">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-6156" data-mp="112ja0dmaf2kj">
                Module
              </th>
              {ACTIONS.map(a => <th key={a} className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center w-28" data-id="element-6157" data-mp="1fsz28cd4fas7">
                  {a}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-6158" data-mp="1jukhu5h1i363">
            {MODULES.map(m => <tr key={m.id} className="hover:bg-slate-50" data-id="element-6159" data-mp="23f2op3obzgec">
                <td className="px-4 py-3 text-sm font-medium text-slate-800" data-id="element-6160" data-mp="28dw0mrnyyzce">
                  {m.label}
                </td>
                {ACTIONS.map(a => {
              const key = `${selectedRole}:${m.id}:${a}`;
              return <td key={a} className="px-4 py-3 text-center" data-id="element-6161" data-mp="1yhj5dpcs0dfj">
                      <div className="flex justify-center" data-id="element-6162" data-mp="1kn9u54tm5n4i">
                        <Switch checked={!!permissions[key]} onChange={() => togglePerm(key)} data-id="element-6163" data-mp="1pei5i6ei7ikb" />
                      </div>
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end" data-id="element-6164" data-mp="11qib8whzha0z">
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-6165" data-mp="11rjqld618ioi">
          Enregistrer les autorisations
        </button>
      </div>
    </div>;
  const renderUtilisateurs = () => <div className="space-y-4" data-id="element-6166" data-mp="33logqvzvn2rf">
      <div data-id="element-6167" data-mp="33kmurwhbovux">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-6168" data-mp="33kwjzhujmyzk">Utilisateurs</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-6169" data-mp="2ynwfhbdce4ak">
          Assignez un rôle à chaque utilisateur.
        </p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-6170" data-mp="2uk88atsmhapu">
        <table className="w-full text-left" data-id="element-6171" data-mp="3ra7qlfykrnlm">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-6172" data-mp="3mcpc7a1sgg68">
            <tr data-id="element-6173" data-mp="3mbe8tbc99afn">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-6174" data-mp="3ibdnd6qqnyz3">
                Utilisateur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-6175" data-mp="3dc0ykggb4htv">
                Courriel
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-64" data-id="element-6176" data-mp="21xneznsl6ojn">
                Rôle assigné
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-6177" data-mp="mefsoscx3yeu">
            {users.map(u => <tr key={u.id} className="hover:bg-slate-50" data-id="element-6178" data-mp="mc1y0km5go7t">
                <td className="px-4 py-3" data-id="element-6179" data-mp="qf849s99ifea">
                  <div className="flex items-center gap-3" data-id="element-6180" data-mp="v6q2w9ntc3w3">
                    <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold" data-id="element-6181" data-mp="v8ax1y67tktf">
                      {u.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-sm font-medium text-slate-800" data-id="element-6182" data-mp="1e5lvjgk0wa7t">
                      {u.nom}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-6183" data-mp="3bx6r3qorw7xg">{u.email}</td>
                <td className="px-4 py-3" data-id="element-6184" data-mp="3fxqo8u9cjbjl">
                  <select value={u.roleId} onChange={e => setUsers(prev => prev.map(x => x.id === u.id ? {
                ...x,
                roleId: e.target.value
              } : x))} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm cursor-pointer focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-6185" data-mp="2kmse1od8nzvn">
                    {roles.map(r => <option key={r.id} value={r.id} data-id="element-6186" data-mp="26ucj7auhr85t">
                        {r.nom}
                      </option>)}
                  </select>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  // ---------- ANALYTIQUES ----------
  const renderAnalytiques = () => {
    const totalPerms = roles.reduce((acc, r) => acc + countPerms(r.id), 0);
    const kpis = [{
      label: 'Rôles actifs',
      value: String(roles.filter(r => r.statut === 'actif').length),
      icon: ShieldCheckIcon
    }, {
      label: 'Utilisateurs',
      value: String(users.length),
      icon: UsersIcon
    }, {
      label: 'Autorisations accordées',
      value: String(totalPerms),
      icon: KeyRoundIcon
    }, {
      label: 'Rôles configurés',
      value: String(roles.length),
      icon: UserCogIcon
    }];
    const maxUsers = Math.max(...roles.map(r => countUsers(r.id)), 1);
    return <div className="space-y-8" data-id="element-6187" data-mp="8fokwfrxpsst">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6188" data-mp="2o4zqx8j7q0mf">
          {kpis.map(k => {
          const Icon = k.icon;
          return <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-6189" data-mp="2t5468v8dwpb2">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center mb-3" data-id="element-6190" data-mp="toriqq81a9r2">
                  <Icon className="w-5 h-5" data-id="element-6191" data-mp="owznw3huhhv4" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A]" data-id="element-6192" data-mp="kw62bgrvkf5e">
                  {k.value}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-6193" data-mp="fxmdn8lwcp9c">{k.label}</div>
              </div>;
        })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-6194" data-mp="ax8hooxkl5ro">
          <h3 className="text-base font-bold text-[#0F172A] mb-4" data-id="element-6195" data-mp="5y5aaxu4angj">
            Répartition des utilisateurs par rôle
          </h3>
          <div className="space-y-3" data-id="element-6196" data-mp="1ywihyin0znm">
            {roles.map(r => {
            const c = countUsers(r.id);
            return <div key={r.id} className="flex items-center gap-3" data-id="element-6197" data-mp="1wbculfy4780e">
                  <span className="text-sm text-slate-600 w-56 truncate" data-id="element-6198" data-mp="1wc4ehticnhau">
                    {r.nom}
                  </span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-6199" data-mp="3j82n8plun9zu">
                    <div className="h-full bg-[#4F46E5] rounded-full" style={{
                  width: `${c / maxUsers * 100}%`
                }} data-id="element-6200" data-mp="3j5pvxlu7bna4"></div>
                  </div>
                  <span className="text-sm font-semibold text-slate-700 w-6 text-right" data-id="element-6201" data-mp="2vbf14k9cj9ht">
                    {c}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  // ---------- CONFIGURER ----------
  const configSections: {
    id: ConfigSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
    desc: string;
  }[] = [{
    id: 'securite',
    label: 'Sécurité',
    icon: LockIcon,
    desc: 'Politique de mot de passe et de session'
  }, {
    id: 'authentification',
    label: 'Authentification',
    icon: FingerprintIcon,
    desc: 'Méthodes de connexion et MFA'
  }, {
    id: 'journalisation',
    label: 'Journalisation',
    icon: ScrollTextIcon,
    desc: 'Audit et conservation des journaux'
  }];
  const [mfa, setMfa] = useState(true);
  const [sso, setSso] = useState(false);
  const [auditLog, setAuditLog] = useState(true);
  const renderConfigContent = () => {
    if (configSection === 'securite') {
      return <div className="space-y-5 max-w-lg" data-id="element-6202" data-mp="dxffvqhanlcp">
          <div data-id="element-6203" data-mp="e0uw4wmpf0fp">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-6204" data-mp="ry9symfaeahp">
              Longueur minimale du mot de passe
            </label>
            <input type="number" defaultValue={12} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-6205" data-mp="nxzjv0w773ym" />
          </div>
          <div data-id="element-6206" data-mp="2pqe1rzyqs8f1">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-6207" data-mp="2fkcrdgyy78dd">
              Expiration de la session (minutes)
            </label>
            <input type="number" defaultValue={30} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-6208" data-mp="2jl6faace4n69" />
          </div>
        </div>;
    }
    if (configSection === 'authentification') {
      return <div className="space-y-4 max-w-lg" data-id="element-6209" data-mp="1vy7yfbkczd7t">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-6210" data-mp="20vgixiszbtc8">
            <div data-id="element-6211" data-mp="1mw71kft1et6o">
              <p className="text-sm font-semibold text-slate-800" data-id="element-6212" data-mp="1mwqknzufbnma">
                Authentification à deux facteurs (MFA)
              </p>
              <p className="text-xs text-slate-500" data-id="element-6213" data-mp="3h9gigsnwxgyy">
                Exiger un second facteur à la connexion.
              </p>
            </div>
            <Switch checked={mfa} onChange={() => setMfa(!mfa)} data-id="element-6214" data-mp="3m7i1jdlc9gaf" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-6215" data-mp="tmthxm0lnrn8">
            <div data-id="element-6216" data-mp="yebgk3f5hg51">
              <p className="text-sm font-semibold text-slate-800" data-id="element-6217" data-mp="yetlrpjsj4p3">
                Authentification unique (SSO)
              </p>
              <p className="text-xs text-slate-500" data-id="element-6218" data-mp="jn2kbbgqswix">
                Connexion via le fournisseur d'identité de la ville.
              </p>
            </div>
            <Switch checked={sso} onChange={() => setSso(!sso)} data-id="element-6219" data-mp="1ceum93w58a41" />
          </div>
        </div>;
    }
    return <div className="space-y-4 max-w-lg" data-id="element-6220" data-mp="1hcdyjy9a6ctc">
        <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-6221" data-mp="13d4ufwycn95q">
          <div data-id="element-6222" data-mp="17eq293w10x92">
            <p className="text-sm font-semibold text-slate-800" data-id="element-6223" data-mp="17e6uuh405r9a">
              Journal d'audit
            </p>
            <p className="text-xs text-slate-500" data-id="element-6224" data-mp="3o33xpt4g8u20">
              Enregistrer toutes les actions des administrateurs.
            </p>
          </div>
          <Switch checked={auditLog} onChange={() => setAuditLog(!auditLog)} data-id="element-6225" data-mp="3s2umxsebv24f" />
        </div>
        <div data-id="element-6226" data-mp="3kjlfw5tanfij">
          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-6227" data-mp="3kn99f4yqo1x9">
            Durée de conservation des journaux (jours)
          </label>
          <input type="number" defaultValue={365} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-6228" data-mp="3uiko5na5d950" />
        </div>
      </div>;
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6229" data-mp="2wvvfwvouq0ap">
      {/* Header */}
      <div className="mb-8" data-id="element-6230" data-mp="2wu0rh7edfq1v">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-id="element-6231" data-mp="3bl9po4wcajqm">
          <div className="flex items-center gap-3" data-id="element-6232" data-mp="36ub5uvc573im">
            <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center" data-id="element-6233" data-mp="36sqbp6tqpmla">
              <SettingsIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-6234" data-mp="2j2m8v1odlqvc" />
            </div>
            <div data-id="element-6235" data-mp="2e4kpefn1fpjc">
              <h1 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-6236" data-mp="2e53xl4mvyzk6">
                Paramètres
              </h1>
              <p className="text-sm text-slate-500" data-id="element-6237" data-mp="33ror50ws05bp">
                Système d'autorisations par rôles
              </p>
            </div>
          </div>
          <nav aria-label="Sections" className="flex flex-wrap items-center gap-2" data-id="element-6238" data-mp="3cn4jtelsoefp">
            {tabs.map(t => <button key={t.id} onClick={() => setActiveSubTab(t.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === t.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-6239" data-mp="2kund6ql8uuvm">
                {t.label}
              </button>)}
          </nav>
        </div>
      </div>

      {activeSubTab === 'analytiques' ? renderAnalytiques() : activeSubTab === 'configurer' ? <div className="flex flex-col lg:flex-row gap-6" data-id="element-6240" data-mp="1ym8d5xryueaa">
          <div className="lg:w-60 flex-shrink-0" data-id="element-6241" data-mp="1ylou2dqkxjuo">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-6242" data-mp="1tmlrczy2nppv">
              {configSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setConfigSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${configSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-6243" data-mp="1jy3sio9crsov">
                    <Icon className={`w-4 h-4 ${configSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-6244" data-mp="10yzmzph0sh1y" />
                    {s.label}
                  </button>;
          })}
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-6245" data-mp="2vao8yblo5jvu">
            <div className="mb-6" data-id="element-6246" data-mp="33b3xagy9hae4">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-6247" data-mp="38c04b3dwtcnr">
                {configSections.find(s => s.id === configSection)?.label}
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-6248" data-mp="2thdbxkxflg2n">
                {configSections.find(s => s.id === configSection)?.desc}
              </p>
            </div>
            {renderConfigContent()}
            <div className="mt-8" data-id="element-6249" data-mp="2kkukkd4um086">
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-6250" data-mp="2pdnowzwen6jq">
                Enregistrer
              </button>
            </div>
          </div>
        </div> :
    // GÉRER
    <div className="flex flex-col lg:flex-row gap-6" data-id="element-6251" data-mp="2fl9kfzdxzltv">
          <div className="lg:w-60 flex-shrink-0" data-id="element-6252" data-mp="21p4h0lh9ovs6">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-6253" data-mp="25pevynn1ewj6">
              {gererSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setGererSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${gererSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-6254" data-mp="1i2ksklmsw33d">
                    <Icon className={`w-4 h-4 ${gererSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-6255" data-mp="210x7ko0e876v" />
                    {s.label}
                  </button>;
          })}
              <div className="px-4 pt-3 pb-1" data-id="element-6256" data-mp="2arrululgratd">
                <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-6257" data-mp="2at2xls734ejf">
                  1. Créez un rôle · 2. Accordez des autorisations · 3.
                  Assignez-le à des usagers.
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8 min-w-0" data-id="element-6258" data-mp="1hfjxpe8c8b94">
            {gererSection === 'roles' && renderRoles()}
            {gererSection === 'autorisations' && renderAutorisations()}
            {gererSection === 'utilisateurs' && renderUtilisateurs()}
          </div>
        </div>}
    </div>;
}